import { createContext, useContext, ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import type { Billing, BillingFeatures, BillingLimits, PageProps } from '@/types'

interface BillingContextValue {
  billing: Billing | null
  plan: 'free' | 'basic' | 'pro'
  isFreePlan: boolean
  canUse: (feature: keyof BillingFeatures) => boolean
  isAtLimit: (limitKey: keyof BillingLimits, currentCount: number) => boolean
  isUnlimited: (limitKey: keyof BillingLimits) => boolean
  usagePercentage: number
  isUsageWarning: boolean
  isUsageCritical: boolean
}

const BillingContext = createContext<BillingContextValue | null>(null)

export function BillingProvider({ children }: { children: ReactNode }) {
  const { billing } = usePage<PageProps>().props

  const plan = billing?.plan ?? 'free'
  const isFreePlan = plan === 'free'

  const canUse = (feature: keyof BillingFeatures): boolean => {
    if (!billing) return false
    return billing.features[feature] ?? false
  }

  const isAtLimit = (limitKey: keyof BillingLimits, currentCount: number): boolean => {
    if (!billing) return true
    const limit = billing.limits[limitKey]
    if (limit === 'unlimited' || limit === null || limit === undefined) return false
    return currentCount >= limit
  }

  const isUnlimited = (limitKey: keyof BillingLimits): boolean => {
    if (!billing) return false
    return billing.limits[limitKey] === 'unlimited'
  }

  const usagePercentage = billing?.usage.percentage ?? 0
  const isUsageWarning = usagePercentage >= 80 && usagePercentage < 100
  const isUsageCritical = usagePercentage >= 100

  const value: BillingContextValue = {
    billing,
    plan,
    isFreePlan,
    canUse,
    isAtLimit,
    isUnlimited,
    usagePercentage,
    isUsageWarning,
    isUsageCritical,
  }

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  )
}

export function useBilling(): BillingContextValue {
  const context = useContext(BillingContext)
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider')
  }
  return context
}
